import _ from 'underscore';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { LabelsColumn } from '@bingads-webui-campaign/labels';
import { ColumnsConfigBuilder } from '@bingads-webui-campaign/summary-grid-helper';
import { adsMetricCategoriesFactory } from '@bingads-webui/custom-columns-shared-config';

import {
  CampaignColumn,
  DeliveryColumn,
  ColumnClicks,
  ColumnImpressions,
  ColumnCTR,
  ColumnAverageCPC,
  ColumnSpend,
  ColumnAveragePosition,
  ColumnConversions,
  ColumnConversionsCredit,
  ColumnViewThroughConversions,
  ColumnViewThroughConversionsCredit,
  ColumnTopImpressionRate,
  ColumnAbsoluteTopImpressionRate,
} from '@bingads-webui-campaign/campaignui-common-columns';

import { StatusIconColumn } from './attributes/status-icon';
import { AdGroupColumn } from './attributes/ad-group';
import { AdIdColumn } from './attributes/ad-id';
import { AdTypeColumn } from './attributes/ad-type';
import { DevicePreferenceColumn } from './attributes/device-preference';
import { AdPreviewColumn } from './attributes/ad-preview';
import { AdsByBingColumn } from './attributes/ads-by-bing';
import { AdStrengthColumn } from './attributes/ad-strength';

const { Columns, SelectedColumns } = globalContext.require('component/bingads-grid/service/column-chooser-preferences');
const { CustomColumns } = globalContext.require('component/bingads-grid/service/custom-column-preferences');
const { LegacyColumns, LegacySelectedColumns } = globalContext.require('component/bingads-grid/service/legacy-column-chooser-preferences');
const { LegacyGridFlag } = globalContext.require('component/bingads-grid/service/legacy-grid-flag-preferences');
const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/ads-tab');
const config = globalContext.require('component/config/index');

let builder = null;
let lastCampaignId;
let lastAdgroupId;
const Permissions = config.get('Permissions');
const {
  IsAdsByBingEnabled: showAdsByBingColumn,
  IsViewThroughConversionEnabled,
  IsCustomColumnsInAdsSupported,
  IsAvgPositionDeprecationPhase1Enabled,
  IsAvgPositionDeprecationPhase2Enabled,
  IsViewThroughConversionsRevenueEnabled,
  dynamic = {},
} = Permissions;
const {
  PartialConversions: IsPartialConversionsEnabled,
  ColumnConversion: IsColumnConversionEnabled,
  ConvValueRules: isConversionValueRuleColumnsEnabled,
  AdStrengthTestingNoPanel,
} = dynamic;

const usePartialConversions = IsPartialConversionsEnabled && IsColumnConversionEnabled;

export function getBuilder({ campaignId, adGroupId }) {
  if (builder === null || campaignId !== lastCampaignId || adGroupId !== lastAdgroupId) {
    lastAdgroupId = adGroupId;
    lastCampaignId = campaignId;
    builder = new ColumnsConfigBuilder({ Permissions })
      .addCategory('Attribute', category => category.addColumns(_.compact([
        AdPreviewColumn,
        _.isUndefined(campaignId) && CampaignColumn,
        _.isUndefined(adGroupId) && AdGroupColumn,
        DeliveryColumn,
        AdIdColumn,
        AdTypeColumn,
        DevicePreferenceColumn,
        LabelsColumn,
        showAdsByBingColumn && AdsByBingColumn,
        !AdStrengthTestingNoPanel && AdStrengthColumn,
      ])))
      .addPerformanceCategoryForAd({
        IsAvgPositionDeprecationPhase2Enabled,
        IsAvgPositionDeprecationPhase1Enabled,
      })
      .addConversionsCategory({
        IsViewThroughConversionEnabled,
        IsViewThroughConversionsRevenueEnabled,
        withPartialConversions: IsPartialConversionsEnabled,
        IsColumnConversionEnabled,
        isConversionValueRuleColumnsEnabled,
      });

    builder
      .setLockedColumns(_.compact([
        StatusIconColumn,
        AdPreviewColumn,
        _.isUndefined(campaignId) && CampaignColumn,
        _.isUndefined(adGroupId) && AdGroupColumn,
        !AdStrengthTestingNoPanel && AdStrengthColumn,
      ]))
      .setSelectedColumns(_.compact([
        DeliveryColumn,
        ColumnClicks,
        ColumnImpressions,
        ColumnCTR,
        ColumnAverageCPC,
        ColumnSpend,
        usePartialConversions && ColumnConversionsCredit,
        !usePartialConversions && ColumnConversions,
        usePartialConversions && ColumnViewThroughConversionsCredit,
        !usePartialConversions && ColumnViewThroughConversions,
        !IsAvgPositionDeprecationPhase1Enabled && ColumnAveragePosition,
        LabelsColumn,
        showAdsByBingColumn && AdsByBingColumn,
        IsAvgPositionDeprecationPhase1Enabled && ColumnTopImpressionRate,
        IsAvgPositionDeprecationPhase1Enabled && ColumnAbsoluteTopImpressionRate,
        !AdStrengthTestingNoPanel && AdStrengthColumn,
      ]));
  }
  return builder;
}

export function getColumnsConfig(options) {
  return getBuilder(options.scope).getColumnsConfig(options);
}

const nameFromLegacy = {
  StatusIcon: 'Status',
  Preview: 'AdPreview',
  AdGroupName: 'AdGroup',
  CampaignName: 'Campaign',
  DeliveryStatus: 'Delivery',
  ID: 'AdID',
  ClickCount: 'Clicks',
  ImpressionCount: 'Impressions',
  ClickThrough: 'CTR',
  TotalSpendAmount: 'Spend',
  ConversionCount: 'Conversions',
};

export function getColumnChooserConfig({
  scope,
  filterService,
  isForVNext = false,
  deps,
}) {
  let customColumnConfig;

  if (IsCustomColumnsInAdsSupported) {
    const { metricCategories, segmentCategories } = adsMetricCategoriesFactory(Permissions);

    customColumnConfig = {
      enabled: true,
      metricCategories,
      segmentCategories,
      filterService,
    };
  }

  return _.defaults(getBuilder(scope).getColumnChooserConfig({
    patchQuery: true,
    Columns,
    SelectedColumns,
    LegacyColumns,
    LegacySelectedColumns,
    LegacyGridFlag,
    nameFromLegacy,
    i18n,
    CustomColumns,
    customColumnConfig,
    permissions: Permissions,
  }), { isForVNext, deps });
}
